import { getCovers } from '@/lib/api/coverApi'
import isNotNil from '@/lib/utils/isNotNil'
import {
  BaseFileEnum,
  type FolderCoverFragment,
  type FormCoverFragment,
  type GetCoversQueryVariables,
} from '@/typings/domain'
import {
  createAsyncThunk,
  createEntityAdapter,
  createSelector,
  createSlice,
  type Draft,
  type PayloadAction,
} from '@reduxjs/toolkit'
import type { RootState } from '../store'
import type { QueryState } from './queryStateType'

export type Cover = FormCoverFragment | FolderCoverFragment

export enum CoversTagKey {
  RecentlyActive = 'recently_active',
  RecentlyCreated = 'recently_created',
  MyBaseFiles = 'desktop',
  SharedForms = 'shared_forms',
  Favorates = 'favorites_forms',
  Participated = 'participated',
  Trash = 'trash',
}

type PageInfo = {
  hasNextPage: boolean
  endCursor?: string | null
}

type CoversMeta = { coverIds: string[] } & QueryState & { pageInfo: PageInfo }
type CoversMetaByBaseFileType = Record<BaseFileEnum, CoversMeta>

type CoversMetaState = {
  activeTag: CoversTagKey
  metaByTag: Record<CoversTagKey, CoversMetaByBaseFileType>
  metaByFolderToken: Record<string, CoversMetaByBaseFileType>
  metaOfSearch: CoversMetaByBaseFileType
}

const coverAdapter = createEntityAdapter<Cover, string>({
  selectId: cover => cover.token,
})

function buildInitialMeta(): CoversMeta {
  return {
    coverIds: [],
    isLoading: false,
    loaded: false,
    error: null,
    pageInfo: {
      hasNextPage: false,
      endCursor: '',
    },
  }
}

function buildInitialMetaByBaseFileType(): CoversMetaByBaseFileType {
  return {
    [BaseFileEnum.Form]: buildInitialMeta(),
    [BaseFileEnum.FormFolder]: buildInitialMeta(),
  }
}

function updateMetaStateOnFetchingCoversFulfilled(
  metaState: Draft<CoversMeta>,
  payload: FetchCoversThunkResult,
  reset?: boolean
) {
  const coverIds = payload.covers.map(cover => cover.token)
  metaState.coverIds = reset ? coverIds : [...metaState.coverIds, ...coverIds]
  metaState.loaded = true
  metaState.isLoading = false
  metaState.pageInfo = payload.pageInfo
}

export const coverSlice = createSlice({
  name: 'cover',
  initialState: coverAdapter.getInitialState<CoversMetaState>({
    activeTag: CoversTagKey.RecentlyActive,
    metaByTag: {
      [CoversTagKey.RecentlyActive]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.RecentlyCreated]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.MyBaseFiles]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.SharedForms]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.Favorates]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.Participated]: buildInitialMetaByBaseFileType(),
      [CoversTagKey.Trash]: buildInitialMetaByBaseFileType(),
    },
    metaByFolderToken: {},
    metaOfSearch: buildInitialMetaByBaseFileType(),
  }),
  reducers: {
    setActiveTag: (state, action: PayloadAction<CoversTagKey>) => {
      state.activeTag = action.payload
    },
    resetCoversMetaStateOfSearch: state => {
      state.metaOfSearch = buildInitialMetaByBaseFileType()
    },
  },
  extraReducers: builder => {
    builder
      .addCase(fetchCoversByTagThunk.pending, (state, action) => {
        const { type, tag } = action.meta.arg
        state.metaByTag[tag][type].isLoading = true
      })
      .addCase(fetchCoversByTagThunk.fulfilled, (state, action) => {
        coverAdapter.upsertMany(state, action.payload.covers)
        const { type, tag, reset } = action.meta.arg
        const metaState = state.metaByTag[tag][type]
        updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
      })
      .addCase(fetchCoversByTagThunk.rejected, (state, action) => {
        const { type, tag } = action.meta.arg
        const metaState = state.metaByTag[tag][type]
        metaState.error = action.error.message ?? 'Failed to fetch covers'
        metaState.isLoading = false
      })
      .addCase(fetchCoversByFolderThunk.pending, (state, action) => {
        const { type, folderToken } = action.meta.arg
        if (!state.metaByFolderToken[folderToken]) {
          state.metaByFolderToken[folderToken] = buildInitialMetaByBaseFileType()
        }
        state.metaByFolderToken[folderToken][type].isLoading = true
      })
      .addCase(fetchCoversByFolderThunk.fulfilled, (state, action) => {
        coverAdapter.upsertMany(state, action.payload.covers)
        const { type, folderToken, reset } = action.meta.arg
        const metaState = state.metaByFolderToken[folderToken]?.[type]
        if (metaState) {
          updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
        }
      })
      .addCase(fetchCoversByFolderThunk.rejected, (state, action) => {
        const { type, folderToken } = action.meta.arg
        const metaState = state.metaByFolderToken[folderToken]?.[type]
        if (metaState) {
          metaState.error = action.error.message ?? 'Failed to fetch covers'
          metaState.isLoading = false
        }
      })
      .addCase(fetchCoversByKeywordThunk.pending, (state, action) => {
        const { type } = action.meta.arg
        state.metaOfSearch[type].isLoading = true
      })
      .addCase(fetchCoversByKeywordThunk.fulfilled, (state, action) => {
        coverAdapter.upsertMany(state, action.payload.covers)
        const { type, reset } = action.meta.arg
        const metaState = state.metaOfSearch[type]
        updateMetaStateOnFetchingCoversFulfilled(metaState, action.payload, reset)
      })
      .addCase(fetchCoversByKeywordThunk.rejected, (state, action) => {
        const { type } = action.meta.arg
        const metaState = state.metaOfSearch[type]
        metaState.error = action.error.message ?? 'Failed to fetch covers'
        metaState.isLoading = false
      })
  },
})

function pageSizeFor(type: BaseFileEnum) {
  return type === BaseFileEnum.FormFolder ? 100 : 20
}

const coversVariablesByTag: Record<Exclude<CoversTagKey, CoversTagKey.Trash>, GetCoversQueryVariables> = {
  [CoversTagKey.RecentlyActive]: {
    tagId: 'desktop',
    sort: 'last_entry_created_at',
    flatten: true,
  },
  [CoversTagKey.RecentlyCreated]: {
    tagId: 'desktop',
    sort: 'created_at',
    flatten: true,
  },
  [CoversTagKey.MyBaseFiles]: {
    tagId: 'desktop',
    sort: 'created_at',
  },
  [CoversTagKey.SharedForms]: {
    tagId: 'shared_forms',
    sort: 'created_at',
  },
  [CoversTagKey.Favorates]: {
    tagId: 'favorites_forms',
    sort: 'created_at',
  },
  [CoversTagKey.Participated]: {
    participated: true,
  },
}

type FetchCoversByTagThunkArg = {
  type: BaseFileEnum
  tag: CoversTagKey
  reset?: boolean
}
type FetchCoversThunkResult = {
  covers: Cover[]
  pageInfo: PageInfo
}
export const fetchCoversByTagThunk = createAsyncThunk<
  FetchCoversThunkResult,
  FetchCoversByTagThunkArg,
  { state: RootState }
>('cover/fetchByTag', async ({ type, tag, reset }, thunkAPI) => {
  // FIXME: fetch trash forms
  if (tag === CoversTagKey.Trash) {
    return {
      covers: [],
      pageInfo: { hasNextPage: false, endCursor: null },
    }
  }

  const after = reset ? null : thunkAPI.getState().cover.metaByTag[tag][type].pageInfo.endCursor

  const { data } = await getCovers({ ...coversVariablesByTag[tag], first: pageSizeFor(type), type, after })

  return data.formCovers?.nodes && data.formCovers?.pageInfo
    ? { covers: data.formCovers.nodes.filter(isNotNil) as Cover[], pageInfo: data.formCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch covers' })
})

type FetchCoversByFolderThunkArg = {
  type: BaseFileEnum
  folderToken: string
  reset?: boolean
}
export const fetchCoversByFolderThunk = createAsyncThunk<
  FetchCoversThunkResult,
  FetchCoversByFolderThunkArg,
  { state: RootState }
>('cover/fetchByFolder', async ({ type, folderToken, reset }, thunkAPI) => {
  const after = reset ? null : thunkAPI.getState().cover.metaByFolderToken[folderToken]?.[type].pageInfo.endCursor

  const { data } = await getCovers({ folderId: folderToken, first: pageSizeFor(type), type, after })
  return data.formCovers?.nodes && data.formCovers?.pageInfo
    ? { covers: data.formCovers.nodes.filter(isNotNil) as Cover[], pageInfo: data.formCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch covers' })
})

type FetchCoversByKeywordThunkArg = {
  keyword: string
  type: BaseFileEnum
  reset?: boolean
}
export const fetchCoversByKeywordThunk = createAsyncThunk<
  FetchCoversThunkResult,
  FetchCoversByKeywordThunkArg,
  { state: RootState }
>('cover/fetchByKeyword', async ({ keyword, type, reset }, thunkAPI) => {
  const after = reset ? null : thunkAPI.getState().cover.metaOfSearch[type].pageInfo.endCursor

  const { data } = await getCovers({ keyword, first: pageSizeFor(type), type, after })
  return data.formCovers?.nodes && data.formCovers?.pageInfo
    ? { covers: data.formCovers.nodes.filter(isNotNil) as Cover[], pageInfo: data.formCovers.pageInfo }
    : thunkAPI.rejectWithValue({ message: 'Failed to fetch covers' })
})

export type CoversFetchingKey =
  | { tag: CoversTagKey; folderToken?: never; byKeyword?: never }
  | { folderToken: string; tag?: never; byKeyword?: never }
  | { byKeyword: true; tag?: never; folderToken?: never }

export const selectCoversMetaState = createSelector(
  (state: RootState) => state.cover,
  (_: RootState, key: CoversFetchingKey) => key,
  (_: RootState, _key: CoversFetchingKey, type: BaseFileEnum) => type,
  (coverState, { tag, folderToken, byKeyword }, type) => {
    if (byKeyword) {
      return coverState.metaOfSearch[type]
    }
    if (tag) {
      return coverState.metaByTag[tag][type]
    }
    return coverState.metaByFolderToken[folderToken]?.[type] ?? buildInitialMeta()
  }
)

export const { selectAll: selectAllCovers, selectById: selectCoverById } = coverAdapter.getSelectors()
export const { setActiveTag, resetCoversMetaStateOfSearch } = coverSlice.actions

export default coverSlice.reducer
